import * as Sentry from "@sentry/react";
import { useT } from "@transifex/react";
import type { CreateSchoolClassOptions, RTContextProps, SilentErrorOptions } from "p6m-contexts";
import type { AeaExerciseModel, AeaGroupedExercisesContent, SchoolClass } from "p6m-p6u";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { CreationModes } from "../constants/CreationFlowConstants";
import { MAX_NAME_LENGTH } from "../constants/LimitConstants";
import { SchoolClassVisibilities } from "../enums/schoolClasses";
import { ShareModes } from "../enums/shareFlow";
import { resolveShareModeWithLegacy } from "../helpers/LegacyTest";
import { deleteRequest, getRequest, postRequest, putRequest } from "../helpers/networkHelper";
import {
    buildOwnSubjectMetadataJson,
    extractShareLinkFromCreateResponse,
    mergeAndFilterCardsTestListResponse,
    processExercisesListResponse,
    resolveArticleUuid,
    resolveMetadataJson,
} from "../helpers/UserExercises";
import { syncVocabTrainingSchoolClassMetadata } from "../helpers/VocabTrainingMetadataSync";
import { formatTitleDate } from "../helpers/VocabTrainingTitle";
import { toClassVisibilityProp, toIsFirstClassProps } from "../helpers/analyticsEventProps";
import { useLogger } from "../hooks/useLogger";
import {
    deleteTestToleratingMissing,
    deleteVocabTrainingMetadata,
    getAllVocabTrainingMetadata,
    getTeacherSharedTests,
    getUserTests,
    getVocabTrainingMetadata,
    recreateVocabTrainingMetadataWithoutSchoolClass,
    shareVocabTrainingMetadata,
    type VocabTrainingMetadataPayload,
} from "../networking/tests";
import { useGeneralContext } from "./AppGeneralContext";
import { useBookContext } from "./BookContext";
import { useTestCreationContext } from "./TestCreationContext";

const RTContext = React.createContext<RTContextProps | undefined>(undefined);

// Dedupes concurrent class reloads. The title bar mounts on several /create pages and each triggers
// a reload when the list is still empty; without this guard those overlap into redundant requests.
// `ok` distinguishes a genuinely empty class list from a failed fetch — both yield an empty array,
// but only the former means "this teacher has no classes" (see schoolClassesLoadError).
let inFlightReloadSchoolClasses: Promise<{ classes: SchoolClass[]; ok: boolean }> | null = null;

function useRTContext() {
    const context = React.useContext(RTContext);

    if (!context) {
        throw new Error("RTContext must be used inside the provider");
    }
    const {
        userId,
        setCreatingMode,
        makePxpRequest,
        setIsDataBeingLoaded,
        hasVocabTraining,
        setHasVocabTraining,
        creatingForSchoolClassId,
    } = useGeneralContext();

    const {
        testName,
        vocabularyDrawerContent,
        selectedBookData,
        setSelectedBookData,
        setTestName,
        setSelectedTitleTag,
        setPreviouslyUsedCardsIds,
        setPreviouslyUsedCardsClassName,
        selectedUserSubject,
        setSelectedUserSubject,
        setUserTests,
        userUsedOwnSubjectsInfo,
        loadUserOwnSubjectUUIDsInfoIntoState,
        setShouldSubjectSelectionBeDisabled,
        resetAppStatus,
        reloadUserSubjects,
        openWarningModal,
    } = useTestCreationContext();

    const { userUsedBooksInfo } = useBookContext();
    const { logEvent } = useLogger();

    const history = useHistory();
    const t = useT();

    const t_errorInsertingClass = t("There was an error while creating the new class.", {
        _tags: "CreateTest,CreateClass",
    });
    const t_errorDeletingClass = t("There was an error while deleting the Class.", {
        _tags: "CreateTest,CreateClass",
    });
    const t_cantUpdateClass = t("Error while updating the class.", { _tags: "CreateTest,CreateClass" });
    const t_errorSwitchingClassVisibility = t("There was an error while switching the class type.", {
        _tags: "SchoolClasses, EditClassPopup",
    });
    const t_deleteExerciseError = t("There was an error while deleting the Exercise.", {
        _tags: "CreateTest,CreateClass",
    });
    const t_problemLoadingClasses = t("There was an error while loading your Classes.", {
        _tags: "CreateTest,CreateClass",
    });
    const t_errorDuplicatingVocabTraining = t("There was an error while duplicating the vocabulary training.", {
        _tags: "ClassActivity, RTContext",
    });
    const {
        schoolClasses,
        setSchoolClasses,
        currentExerciseSchoolClassId,
        setCurrentExerciseSchoolClassId,
        setUserExercisesGroupedData,
        isFirstRtHomeLoad,
        setIsFirstRtHomeLoad,
        hasLoadedSchoolClasses,
        setHasLoadedSchoolClasses,
        schoolClassesLoadError,
        setSchoolClassesLoadError,
        setVocabTrainings,
        setVocabTrainingsDataLoaded,
        setVocabTrainingsLoadError,
        vocabMetadata,
        setVocabMetadata,
    } = context;

    async function loadClassesAndExercisesForUser(isCalledFromHome?: boolean) {
        if (userId) {
            const classesResponse = await reloadSchoolClasses();
            const exercisesResponse = await reloadUserExercises();
            await reloadUserSubjects();

            if (isCalledFromHome && isFirstRtHomeLoad) {
                if (classesResponse.length === 0) {
                    startNewRecurringExerciseForGroup("");
                } else if (Object.keys(exercisesResponse || {}).length === 0) {
                    startNewRecurringExerciseForGroup("");
                }
                setIsFirstRtHomeLoad(false);
            }
        }
    }

    async function reloadUserExercises() {
        setIsDataBeingLoaded(true);
        try {
            const exercisesListResponse = await makePxpRequest("GET", "cardsTestList");
            let processedResponse: AeaGroupedExercisesContent = {};
            if (exercisesListResponse.data.replyContent.objects) {
                processedResponse = processExercisesListResponse(
                    exercisesListResponse.data.replyContent.objects,
                    userId
                );
                setUserExercisesGroupedData(processedResponse);
            }
            return processedResponse;
        } finally {
            setIsDataBeingLoaded(false);
        }
    }

    async function getRawVocabTrainings() {
        const exercisesListResponse = await makePxpRequest("GET", "cardsTestList");
        return mergeAndFilterCardsTestListResponse(exercisesListResponse.data.replyContent.objects);
    }

    // Always fetches — no dataLoaded guard. Two RTContextProvider instances exist (see Layout),
    // so trainings created under /create land in the other instance and cached state here can be stale.
    async function fetchAndStoreVocabTrainings(): Promise<Array<AeaExerciseModel> | null> {
        let rawVocabTrainings: Array<AeaExerciseModel>;
        try {
            rawVocabTrainings = await getRawVocabTrainings();
        } catch {
            // Keep any previously loaded trainings so hasVocabTraining doesn't regress
            // and onboarding doesn't reappear when cardsTestList intermittently fails.
            setVocabTrainingsLoadError(true);
            setVocabTrainingsDataLoaded(true);
            return null;
        }

        setVocabTrainingsLoadError(false);
        setVocabTrainings(rawVocabTrainings);
        setVocabTrainingsDataLoaded(true);
        // Single sync point for the AppGeneralContext onboarding flag. Never runs on error,
        // so the flag can't regress on transient failures. A just-created training may still be
        // missing from an eventually-consistent list read and briefly flip the flag back to false —
        // same as the previous per-page sync, healed by the next load.
        setHasVocabTraining(rawVocabTrainings.length > 0);

        // Metadata failure is non-fatal: card falls back to HIDDEN, share/edit flows still work.
        // Skip trainings without a class — badge is suppressed there regardless.
        const trainingIds = rawVocabTrainings.flatMap((vt) => (vt.groupId && vt.id ? [vt.id] : []));
        getAllVocabTrainingMetadata(trainingIds).then(({ data, failedIds }) => {
            // Merge so concurrent optimistic writes from handleSaveVocabVisibility aren't clobbered.
            setVocabMetadata((prev) => ({ ...prev, ...data }));
            if (failedIds.length > 0) {
                logEvent("VTG - Vocab Training Metadata Partial Failure", {
                    failedCount: failedIds.length,
                    totalRequested: trainingIds.length,
                });
            }
        });

        return rawVocabTrainings;
    }

    /**
     * Backfill for metadata rows written before shareLink existed. Best-effort: an empty string lets
     * callers write the metadata without a link rather than fail.
     */
    async function _resolveVocabTrainingShareLink(trainingId: string): Promise<string> {
        try {
            const trainings = await getRawVocabTrainings();
            return trainings.find((vt) => vt.id === trainingId)?.shareLink ?? "";
        } catch (e) {
            console.error("Failed to resolve vocab training shareLink", e);
            return "";
        }
    }

    async function reloadSchoolClasses() {
        if (!userId) {
            return [];
        }

        // Dedupe the network request: start one fetch and let overlapping callers share it. The fetch
        // itself doesn't touch state, so each caller still applies the result to its own provider
        // instance below (two providers can be mounted at once, see Layout).
        if (!inFlightReloadSchoolClasses) {
            inFlightReloadSchoolClasses = (async () => {
                try {
                    const classListResponse = await getRequest("aea/classes/");
                    const classListResponseArray: SchoolClass[] = Object.values(classListResponse.data);
                    const sorted = classListResponseArray.sort((c1: SchoolClass, c2: SchoolClass) => {
                        return (c2.dateInserted || 0) - (c1.dateInserted || 0);
                    });
                    return { classes: sorted, ok: true };
                } catch {
                    openWarningModal(t_problemLoadingClasses);
                    return { classes: [], ok: false };
                }
            })();
        }

        const reload = inFlightReloadSchoolClasses;
        try {
            const { classes: userClasses, ok } = await reload;
            setSchoolClasses(userClasses);
            // Sets as loaded even for an empty/failed result so callers can skip re-fetching on every
            // mount. Guarding on `schoolClasses.length === 0` alone re-fetched on each page nav for
            // teachers with no classes (the list never becomes non-empty).
            setHasLoadedSchoolClasses(true);
            // Tracked separately because the line above cannot distinguish the two: after a failed
            // fetch the state is indistinguishable from "teacher has no classes", which would report
            // is_first_class: true for a teacher who has many.
            setSchoolClassesLoadError(!ok);
            return userClasses;
        } finally {
            // Only clear if no newer reload has replaced this one.
            if (inFlightReloadSchoolClasses === reload) {
                inFlightReloadSchoolClasses = null;
            }
        }
    }

    async function createSchoolClass(
        name: string,
        classVisibility?: SchoolClassVisibilities,
        options?: CreateSchoolClassOptions
    ) {
        try {
            const createClassResponse = await postRequest("aea/classes/", {
                name: name.slice(0, MAX_NAME_LENGTH),
                ...(classVisibility !== undefined && { classVisibility }),
            });
            // Share flows suppress this: their rollback deletes the class again if a later step
            // fails, so they emit it themselves only once the whole share has succeeded.
            // `schoolClasses` is captured from the render that produced this closure, so it is
            // still the pre-create list even after the reload in the finally below.
            if (!options?.suppressCreatedEvent) {
                logEvent("Created Class", {
                    // groupId is grandfathered — kept so the existing series stays continuous.
                    groupId: createClassResponse.data.token,
                    class_id: createClassResponse.data.token,
                    class_visibility: toClassVisibilityProp(classVisibility ?? SchoolClassVisibilities.PUBLIC),
                    ...toIsFirstClassProps(hasLoadedSchoolClasses && !schoolClassesLoadError, schoolClasses.length),
                });
            }

            return createClassResponse.data.token;
        } catch (e) {
            // Callers only see null, so the network error would otherwise never reach Sentry.
            Sentry.captureException(e);
            if (!options?.silentOnError) {
                openWarningModal(t_errorInsertingClass);
            }
            return null;
        } finally {
            await reloadSchoolClasses();
        }
    }

    async function updateSchoolClass(
        schoolClassId: string,
        newName: string,
        classVisibility?: SchoolClassVisibilities
    ): Promise<boolean> {
        try {
            await putRequest(`aea/classes/?token=${schoolClassId}`, {
                name: newName.slice(0, MAX_NAME_LENGTH),
                ...(classVisibility !== undefined && { classVisibility }),
            });
        } catch (e) {
            openWarningModal(t_cantUpdateClass);
            return false;
        }
        await reloadSchoolClasses();
        return true;
    }

    async function saveUserExercise() {
        const publisherId = selectedBookData.publisherBook?.PublisherID || "";
        // The selectedBookData can be stale (book chosen, then user navigated back and switched to own
        // content), so the source is derived from the cards actually being saved, not from it.
        const hasBookCardsInDrawer = Object.values(vocabularyDrawerContent).some(
            (card) => card.wordContent && "uuid" in card.wordContent
        );
        const isOwnContent = !hasBookCardsInDrawer && !!selectedUserSubject.id;
        const exerciseId = uuidv4();
        let ownWordsCount = 0;
        const cardIds = Object.values(vocabularyDrawerContent).map((card) => {
            if (!card.wordContent) {
                return null;
            }
            if ("uuid" in card.wordContent) {
                return {
                    id: card.wordContent?.uuid,
                    ownerId: publisherId,
                };
            } else if ("isUserCard" in card.wordContent) {
                ownWordsCount++;
                return {
                    id: card.wordContent?.id,
                    ownerId: card.wordContent?.ownerId,
                };
            }

            return null;
        });
        const t_defaultTitle = t("Vocabulary from {date}", {
            _tags: "CreateVocabTraining,VocabTrainingTitleBar",
            date: formatTitleDate(),
        });
        const vocabularyCount = cardIds.filter(Boolean).length;
        // Capped for the testName DB column; shared with the metadata call so both rows store the same name.
        const resolvedTestName = (testName.trim() || t_defaultTitle).slice(0, MAX_NAME_LENGTH);
        const requestParameters: any = {
            testName: resolvedTestName,
            cardIds,
        };

        const effectiveSchoolClassId = currentExerciseSchoolClassId || creatingForSchoolClassId;
        if (effectiveSchoolClassId.length > 0) {
            requestParameters.groupId = effectiveSchoolClassId;
        }

        if (isOwnContent) {
            requestParameters.subjectContentId = {
                id: selectedUserSubject.id,
                ownerId: selectedUserSubject.ownerId,
            };
        } else {
            requestParameters.subjectContentId = {
                id: selectedBookData.band?.UUID,
                ownerId: publisherId,
            };
        }

        const exerciseCreationResponse = await makePxpRequest("POST", "cardsTestWithShare/" + exerciseId, {
            ...requestParameters,
        });
        if (!exerciseCreationResponse.data.httpCode || exerciseCreationResponse.data.httpCode !== 200) {
            return Promise.reject("Error");
        }

        if (userId) {
            try {
                await shareVocabTrainingMetadata({
                    testUuid: exerciseId,
                    teacherId: userId,
                    articleUuid: isOwnContent ? null : selectedBookData.band?.UUID ?? null,
                    // Own content has no articleUuid, so this is what puts the training into recent-articles.
                    json: isOwnContent ? buildOwnSubjectMetadataJson(selectedUserSubject) : undefined,
                    schoolClassId: effectiveSchoolClassId || null,
                    testName: resolvedTestName,
                    bookName: isOwnContent ? selectedUserSubject.name || "" : selectedBookData.band?.Name || "",
                    vocabularyCount,
                    shareLink: extractShareLinkFromCreateResponse(exerciseCreationResponse.data),
                    // New trainings start HIDDEN like tests: only the share flow (or the edit-visibility
                    // modal) publishes. A creation-time visibilityDate would skip the share flow entirely,
                    // since the share button jumps straight to the link popup for visible trainings.
                    visibilityDate: null,
                });
            } catch (e) {
                // This is non-blocking: the training is already persisted by cardsTestWithShare; recent-articles
                // registration is best-effort and must not break the save or navigation to /assistent.
                console.error("Failed to register vocab training metadata for recent-articles", e);
            }
        }

        const isFirstTraining = !hasVocabTraining;
        setHasVocabTraining(true);

        logEvent("Created Exercise", {
            // exerciseId/groupId are grandfathered — kept so the existing series stay continuous.
            // training_id/class_id are the canonical names the class and share events use.
            exerciseId,
            training_id: exerciseId,
            groupId: effectiveSchoolClassId || undefined,
            ...(effectiveSchoolClassId ? { class_id: effectiveSchoolClassId } : {}),
            publisherId: isOwnContent ? "" : publisherId,
            bookId: isOwnContent ? undefined : selectedBookData.band?.ID,
            language: isOwnContent ? undefined : selectedBookData.subject?.ID,
            wordsCount: vocabularyCount,
            ownWordsCount,
            onboarding_training_created: true,
        });

        if (isFirstTraining) {
            logEvent("First Training Created", { onboarding_training_created: true });
        }

        resetAppStatus();

        localStorage.setItem("lsRt", exerciseId);

        return Promise.resolve();
    }

    /**
     * The copy always starts HIDDEN — even when it keeps the class — so the teacher publishes it via the share flow.
     * @returns whether the pxp copy exists; metadata registration is best-effort (logged, never surfaced).
     */
    async function duplicateVocabTraining(
        training: AeaExerciseModel,
        newName: string,
        bookName: string,
        schoolClassId?: string
    ): Promise<boolean> {
        const newTrainingId = uuidv4();
        const cardIds = training.cardIds ?? [];
        const resolvedName = newName.slice(0, MAX_NAME_LENGTH);

        let shareLink = "";
        try {
            const creationResponse = await makePxpRequest("POST", `cardsTestWithShare/${newTrainingId}`, {
                testName: resolvedName,
                cardIds,
                subjectContentId: training.subjectContentId,
                ...(schoolClassId ? { groupId: schoolClassId } : {}),
            });
            if (creationResponse.data.httpCode !== 200) {
                throw new Error(`pxp did not confirm duplicating vocab training ${training.id}`);
            }
            shareLink = extractShareLinkFromCreateResponse(creationResponse.data);
        } catch (e) {
            Sentry.captureException(e, { extra: { vocabTrainingId: training.id } });
            openWarningModal(t_errorDuplicatingVocabTraining);
            return false;
        }

        if (userId) {
            try {
                const json = await resolveMetadataJson({
                    training,
                    userId,
                    ownSubjectsInfo: userUsedOwnSubjectsInfo,
                    storedJson: training.id ? vocabMetadata[training.id]?.json : undefined,
                    loadOwnSubjectsInfo: loadUserOwnSubjectUUIDsInfoIntoState,
                });
                await shareVocabTrainingMetadata({
                    testUuid: newTrainingId,
                    teacherId: userId,
                    articleUuid: resolveArticleUuid(training, userId),
                    json,
                    schoolClassId: schoolClassId ?? null,
                    testName: resolvedName,
                    bookName,
                    vocabularyCount: cardIds.length,
                    shareLink,
                    visibilityDate: null,
                });
            } catch (e) {
                console.error("Failed to register duplicated vocab training metadata", e);
                Sentry.captureException(e, {
                    extra: { vocabTrainingId: newTrainingId, sourceTrainingId: training.id },
                });
            }
        }

        logEvent("duplicated vocab training", {
            training_id: newTrainingId,
            source_training_id: training.id,
            ...(schoolClassId ? { class_id: schoolClassId } : {}),
        });

        return true;
    }

    // Resolves whether the new groupId reached pxp — and only that. The metadata sync below is best-effort:
    // it retries and reports to Sentry, but it can still fail while this resolves true, leaving the row's
    // schoolClassId stale (see HALLOFUSS-6639).
    // The share rollback gates a cascading class delete on this, so it must track the pxp write, not the row.
    async function updateVocabTrainingSchoolClass(
        trainingId: string,
        schoolClassId: string | null,
        options?: SilentErrorOptions & { skipMetadataSync?: boolean }
    ): Promise<boolean> {
        try {
            await makePxpRequest("POST", `cardsTest/${trainingId}`, { groupId: schoolClassId });
        } catch (e) {
            // Silent callers (e.g. share flow) handle rollback themselves and need the error to propagate.
            if (options?.silentOnError) throw e;
            openWarningModal(t_cantUpdateClass);
            return false;
        }

        // Share flows POST the full row themselves; see the skipMetadataSync contract.
        if (options?.skipMetadataSync) return true;

        // The trainings list is fetched on demand rather than on every class change; the sync calls this
        // only for rows that actually lack an articleUuid.
        const _deriveArticleUuid = async () => {
            const training = (await getRawVocabTrainings()).find((vt) => vt.id === trainingId);
            return training ? resolveArticleUuid(training, userId) : null;
        };

        // Awaited so the removal branch's cache write lands before callers refresh their lists — a
        // class-less training is excluded from the bulk metadata refresh, so nothing else would settle
        // it. The outcome is deliberately discarded; see the note above this function.
        await syncVocabTrainingSchoolClassMetadata({
            trainingId,
            schoolClassId,
            userId,
            cachedMetadata: vocabMetadata[trainingId],
            getVocabTrainingMetadata,
            shareVocabTrainingMetadata,
            recreateVocabTrainingMetadataWithoutSchoolClass,
            deriveArticleUuid: _deriveArticleUuid,
            resolveShareLink: () => _resolveVocabTrainingShareLink(trainingId),
            setVocabMetadata,
            captureError: (error) => Sentry.captureException(error, { extra: { vocabTrainingId: trainingId } }),
        });

        return true;
    }

    async function deleteUserExercises(exerciseIds: Array<string>) {
        const idsWithErrors = [];
        for (let i = 0; i < exerciseIds.length; i++) {
            const id = exerciseIds[i];
            const exerciseDeletionResponse = await makePxpRequest("DELETE", "cardsTest/" + id);
            if (exerciseDeletionResponse.data.replyContent !== "Done") {
                idsWithErrors.push(id);
            }
        }
        loadClassesAndExercisesForUser();
        return Promise.resolve(idsWithErrors);
    }

    async function deleteVocabTraining(exerciseId: string) {
        setIsDataBeingLoaded(true);
        const deleteExerciseResponse = await deleteUserExercises([exerciseId]);
        if (deleteExerciseResponse.length !== 0) {
            openWarningModal(t_deleteExerciseError);
        } else {
            try {
                await deleteVocabTrainingMetadata(exerciseId);
            } catch (e) {
                // This is non-blocking: the training itself is deleted; removing its recent-articles metadata
                // is best-effort cleanup and must not surface an error to the user.
                console.error("Failed to delete vocab training metadata", e);
            }
        }
        setIsDataBeingLoaded(false);
    }

    // pxp reports a failed delete as a 200 with a non-"Done" body.
    async function _deleteVocabTrainingOnPxp(trainingId: string): Promise<void> {
        const response = await makePxpRequest("DELETE", `cardsTest/${trainingId}`);
        if (response.data.replyContent !== "Done") {
            throw new Error(`pxp did not confirm deleting vocab training ${trainingId}`);
        }
    }

    async function _deleteClassActivities(schoolClassId: string): Promise<boolean> {
        try {
            const rawTrainings = await getRawVocabTrainings();
            const affectedTrainingIds = rawTrainings
                .filter((training) => training.groupId === schoolClassId && training.id)
                .map((training) => training.id!);
            // Delete each training, then drop its metadata. Pairing the two per-id keeps one
            // training's delete failure from skipping metadata cleanup for the others — otherwise the
            // successfully-deleted trainings' metadata rows would orphan permanently.
            const deleteOutcomes = await Promise.allSettled(
                affectedTrainingIds.map(async (id) => {
                    await _deleteVocabTrainingOnPxp(id);
                    await deleteVocabTrainingMetadata(id).catch((e) => {
                        console.error("Failed to delete vocab training metadata", e);
                        Sentry.captureException(e, { extra: { vocabTrainingId: id } });
                    });
                })
            );
            deleteOutcomes.forEach((outcome, index) => {
                if (outcome.status === "rejected") {
                    Sentry.captureException(outcome.reason, {
                        extra: { schoolClassId, vocabTrainingId: affectedTrainingIds[index] },
                    });
                }
            });
            if (deleteOutcomes.some((outcome) => outcome.status === "rejected")) {
                return false;
            }
            if (affectedTrainingIds.length > 0) {
                await reloadUserExercises();
            }
        } catch (e) {
            Sentry.captureException(e, { extra: { schoolClassId } });
            return false;
        }

        try {
            const tests = await getUserTests();
            const affectedTests = tests.filter((test) => test.groupId === schoolClassId);
            // Settled so one failing delete doesn't abandon the rest mid-flight, and only the tests that
            // actually went through are dropped locally — otherwise the UI hides tests still on the backend.
            const deleteOutcomes = await Promise.allSettled(
                affectedTests.map((test) => deleteTestToleratingMissing(String(test.id)))
            );
            const deletedTestIds = new Set(
                affectedTests.filter((_, index) => deleteOutcomes[index].status === "fulfilled").map((test) => test.id)
            );
            setUserTests((prev) => prev.filter((test) => !deletedTestIds.has(test.id)));

            deleteOutcomes.forEach((outcome, index) => {
                if (outcome.status === "rejected") {
                    Sentry.captureException(outcome.reason, {
                        extra: { schoolClassId, testId: affectedTests[index].id },
                    });
                }
            });
            if (deleteOutcomes.some((outcome) => outcome.status === "rejected")) {
                return false;
            }
        } catch (e) {
            Sentry.captureException(e, { extra: { schoolClassId } });
            return false;
        }

        return true;
    }

    async function _deleteSharedTestsForClass(schoolClassId: string): Promise<void> {
        const [tests, sharedTests] = await Promise.all([getUserTests(), getTeacherSharedTests()]);
        const affectedTests = tests.filter(
            (test) =>
                test.groupId === schoolClassId &&
                test.id &&
                sharedTests[test.id] &&
                resolveShareModeWithLegacy(sharedTests[test.id]) !== ShareModes.PRACTICE
        );
        // Settled so one failing delete doesn't abandon the rest mid-flight, and only the tests that actually
        // went through are dropped locally — otherwise the UI hides tests still live on the backend.
        const deleteOutcomes = await Promise.allSettled(
            affectedTests.map((test) => deleteTestToleratingMissing(test.id!))
        );
        const deletedTestIds = new Set(
            affectedTests.filter((_, index) => deleteOutcomes[index].status === "fulfilled").map((test) => test.id)
        );
        setUserTests((prev) => prev.filter((test) => !deletedTestIds.has(test.id)));

        // Rethrow the first failure rather than a synthetic error, so the original response survives.
        const firstFailure = deleteOutcomes.find(
            (outcome): outcome is PromiseRejectedResult => outcome.status === "rejected"
        );
        if (firstFailure) {
            throw firstFailure.reason;
        }
    }

    async function cleanupPrivateToPublic(schoolClassId: string): Promise<boolean> {
        setIsDataBeingLoaded(true);
        try {
            const membersResponse = await getRequest("aea/class-members-class/", { classToken: schoolClassId });
            const members: { id: string }[] = Object.values(membersResponse.data);

            // Members go last: deleting them is irreversible, so every step that can still abort the switch
            // has to run first. Aborting after this point would destroy the students without going public.
            await _deleteSharedTestsForClass(schoolClassId);

            await Promise.all(members.map((member) => deleteRequest("aea/class-members/", { member_id: member.id })));
            return true;
        } catch (e) {
            // Which step failed decides what survived — the caller only learns that something did, and the
            // popup is already closed by then, so this is the only record of it.
            console.error("Failed to clean up class before switching it to public", e);
            Sentry.captureException(e, { extra: { schoolClassId } });
            openWarningModal(t_errorSwitchingClassVisibility);
            return false;
        } finally {
            setIsDataBeingLoaded(false);
        }
    }

    async function deleteSchoolClass(schoolClassId: string, options?: SilentErrorOptions): Promise<boolean> {
        setIsDataBeingLoaded(true);

        try {
            const cleanupSucceeded = await _deleteClassActivities(schoolClassId);
            if (!cleanupSucceeded) {
                if (!options?.silentOnError) openWarningModal(t_errorDeletingClass);
                return false;
            }

            const deleteClassResponse = await deleteRequest("aea/classes/", {
                token: schoolClassId,
            });
            if (deleteClassResponse.data.error) {
                throw new Error(deleteClassResponse.data.error);
            }

            return true;
        } catch (e) {
            if (!options?.silentOnError) openWarningModal(t_errorDeletingClass);
            return false;
        } finally {
            await reloadSchoolClasses();
            setIsDataBeingLoaded(false);
        }
    }

    function startNewRecurringExerciseForGroup(schoolClassId: string, withOwnContent?: boolean) {
        setCreatingMode(CreationModes.RECURRING_TASK);
        setTestName("");
        setSelectedTitleTag(null);
        setPreviouslyUsedCardsIds([]);
        setPreviouslyUsedCardsClassName("");
        setCurrentExerciseSchoolClassId(schoolClassId);

        if (withOwnContent) {
            history.push("/create/select-own-source");
        } else {
            history.push("/create/select-book");
        }
    }

    async function addNewVocabularyToBookInGroup(
        schoolClassId: string,
        contentUuid: string,
        isUserOwnContent: boolean
    ) {
        setCreatingMode(CreationModes.RECURRING_TASK);
        setTestName("");
        setSelectedTitleTag(null);
        setPreviouslyUsedCardsIds([]);
        setPreviouslyUsedCardsClassName("");
        setCurrentExerciseSchoolClassId(schoolClassId);

        if (isUserOwnContent) {
            if (userUsedOwnSubjectsInfo[contentUuid]) {
                setSelectedUserSubject(userUsedOwnSubjectsInfo[contentUuid]);
            } else {
                const userSubjectsResponse = await makePxpRequest("GET", `/${userId}/subjects/${contentUuid}`);
                if (!userSubjectsResponse.data || !userSubjectsResponse.data.replyContent) {
                    throw new Error("No data was loaded");
                }
                setSelectedUserSubject(userSubjectsResponse.data.replyContent);
            }
            setShouldSubjectSelectionBeDisabled(true);
        } else {
            if (userUsedBooksInfo[contentUuid]) {
                setSelectedBookData(userUsedBooksInfo[contentUuid]);
            } else {
                const bandInfoResponse = await getRequest("p6u/get-band-info/", { bandId: contentUuid, isUuid: true });
                if (!bandInfoResponse || !bandInfoResponse.data) {
                    throw new Error("No data was loaded");
                }
                setSelectedBookData(bandInfoResponse.data);
            }
        }

        history.push(isUserOwnContent ? "/create/select-own-source" : "/create/select-vocabulary");
    }

    return {
        ...context,
        loadClassesAndExercisesForUser,
        reloadSchoolClasses,
        createSchoolClass,
        updateSchoolClass,
        saveUserExercise,
        duplicateVocabTraining,
        updateVocabTrainingSchoolClass,
        deleteVocabTraining,
        deleteSchoolClass,
        cleanupPrivateToPublic,
        startNewRecurringExerciseForGroup,
        addNewVocabularyToBookInGroup,
        getRawVocabTrainings,
        fetchAndStoreVocabTrainings,
    };
}

function RTContextProvider(props: any) {
    const [schoolClasses, setSchoolClasses] = useState<Array<SchoolClass>>([]);
    const [currentExerciseSchoolClassId, setCurrentExerciseSchoolClassId] = useState("");
    const [userExercisesGroupedData, setUserExercisesGroupedData] = useState<AeaGroupedExercisesContent>({});
    const [isFirstRtHomeLoad, setIsFirstRtHomeLoad] = useState(true);
    const [hasLoadedSchoolClasses, setHasLoadedSchoolClasses] = useState(false);
    const [schoolClassesLoadError, setSchoolClassesLoadError] = useState(false);
    const [vocabTrainings, setVocabTrainings] = useState<Array<AeaExerciseModel>>([]);
    const [vocabTrainingsDataLoaded, setVocabTrainingsDataLoaded] = useState(false);
    const [vocabTrainingsLoadError, setVocabTrainingsLoadError] = useState(false);
    const [vocabMetadata, setVocabMetadata] = useState<Record<string, VocabTrainingMetadataPayload>>({});

    const value = {
        schoolClasses,
        setSchoolClasses,
        currentExerciseSchoolClassId,
        setCurrentExerciseSchoolClassId,
        userExercisesGroupedData,
        setUserExercisesGroupedData,
        isFirstRtHomeLoad,
        setIsFirstRtHomeLoad,
        hasLoadedSchoolClasses,
        setHasLoadedSchoolClasses,
        schoolClassesLoadError,
        setSchoolClassesLoadError,
        vocabTrainings,
        setVocabTrainings,
        vocabTrainingsDataLoaded,
        setVocabTrainingsDataLoaded,
        vocabTrainingsLoadError,
        setVocabTrainingsLoadError,
        vocabMetadata,
        setVocabMetadata,
    };

    return (
        <RTContext.Provider
            value={value}
            {...props}
        />
    );
}

export { RTContextProvider, useRTContext };
